/**
 * Custom element for dynamically updating "who it's for" text based on variant metafields
 * Specifically for the color-depositing-mask product
 */
class VariantWhoItsFor extends HTMLElement {
  constructor() {
    super();
    this.metafieldKey = this.dataset.variantMetafield;
    this.observer = null;
    this.currentVariantId = null;
    this.variantMetafields = null;
    this.checkTimeout = null;
    this.init();
  }

  init() {
    // Get variant metafields from the embedded script tag immediately
    this.getVariantMetafieldsFromDOM();
    this.setupEventListeners();
    this.setupMutationObserver();
    
    // Initial check with shorter delay
    this.scheduleVariantCheck(100);
  }

  getVariantMetafieldsFromDOM() {
    // Cache the variant metafields from the embedded script tag
    const variantMetafieldsScript = document.querySelector('script[data-variant-metafields]');
    if (variantMetafieldsScript) {
      try {
        this.variantMetafields = JSON.parse(variantMetafieldsScript.textContent);
      } catch (e) {
        // Skip invalid JSON
      }
    }
  }

  setupEventListeners() {
    // Listen for variant option changes with immediate response
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"][name*="option"], select[name*="option"]')) {
        this.scheduleVariantCheck(10);
      }
    });

    // Listen for clicks on variant options
    document.addEventListener('click', (event) => {
      if (event.target.matches('input[type="radio"][name*="option"]')) {
        this.scheduleVariantCheck(10);
      }
    });

    // Listen for any custom variant events
    document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
    document.addEventListener('variantChanged', this.handleVariantChange.bind(this));
    document.addEventListener('product:variant:changed', this.handleVariantChange.bind(this));
  }

  setupMutationObserver() {
    // More targeted observer that only watches the variant ID input
    const variantIdInput = document.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          this.scheduleVariantCheck(0); // Immediate check
        }
      });
    });

    // Only observe the variant ID input for value changes
    this.observer.observe(variantIdInput, {
      attributes: true,
      attributeFilter: ['value']
    });
  }

  scheduleVariantCheck(delay = 0) {
    // Debounce variant checks to avoid excessive calls
    clearTimeout(this.checkTimeout);
    this.checkTimeout = setTimeout(() => {
      this.checkForVariantChange();
    }, delay);
  }

  checkForVariantChange() {
    // Get current variant ID
    const variantIdInput = document.querySelector('input[name="id"]');
    if (!variantIdInput || !variantIdInput.value) {
      return;
    }

    const currentVariantId = variantIdInput.value;
    
    // Only update if variant actually changed
    if (currentVariantId !== this.currentVariantId) {
      this.currentVariantId = currentVariantId;
      this.updateTextForVariant(currentVariantId);
    }
  }

  updateTextForVariant(variantId) {
    // Use cached metafields for instant updates
    if (this.variantMetafields && this.variantMetafields[variantId]) {
      this.updateText(this.variantMetafields[variantId]);
    } else {
      // Fallback to default text
      this.textContent = 'Suitable for all hair types';
    }
  }

  updateText(variant) {
    if (!variant || !this.metafieldKey) {
      this.textContent = 'Suitable for all hair types';
      return;
    }

    const [namespace, key] = this.metafieldKey.split('.');
    
    let newText = 'Suitable for all hair types'; // Default fallback
    
    if (variant.metafields && variant.metafields[namespace] && variant.metafields[namespace][key]) {
      newText = variant.metafields[namespace][key];
    }

    if (this.textContent !== newText) {
      this.textContent = newText;
    }
  }

  handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (variant && variant.id) {
      this.currentVariantId = variant.id.toString();
      this.updateText(variant);
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    clearTimeout(this.checkTimeout);
  }
}

// Register the custom element
if (!customElements.get('variant-who-its-for')) {
  customElements.define('variant-who-its-for', VariantWhoItsFor);
}